const API_URL = import.meta.env.VITE_API_URL;
const MAX_PRINT_IMAGE_CONCURRENCY = 4;
const IMAGE_LOAD_TIMEOUT = 15000;

const isPreparedImageSource = (image) => /^(blob:|data:|https?:\/\/)/i.test(image);

export const getPrintableImageSource = (image) => {
    if (!image) return "";

    const source = String(image);
    if (isPreparedImageSource(source)) return source;

    return `${API_URL.replace(/\/+$/, "")}/${source.replace(/^\/+/, "")}`;
};

const waitForImageDecode = (src) => new Promise((resolve, reject) => {
    if (!src) {
        resolve();
        return;
    }

    const img = new Image();
    let timeoutId;
    let settled = false;

    const finish = (callback, value) => {
        if (settled) return;

        settled = true;
        clearTimeout(timeoutId);
        callback(value);
    };

    timeoutId = setTimeout(() => {
        finish(reject, new Error("Tempo limite ao carregar imagem para impressão."));
    }, IMAGE_LOAD_TIMEOUT);

    img.onload = async () => {
        try {
            if (img.decode) await img.decode();
            finish(resolve);
        } catch (error) {
            finish(reject, error);
        }
    };

    img.onerror = () => {
        finish(reject, new Error("Erro ao carregar imagem para impressão."));
    };

    img.src = src;

    if (img.complete && img.naturalWidth > 0) {
        finish(resolve);
    }
});

const fetchImageAsObjectUrl = async (src) => {
    const response = await fetch(src, {credentials: "include"});
    if (!response.ok) throw new Error(`Erro HTTP ${response.status} ao carregar imagem.`);

    const objectUrl = URL.createObjectURL(await response.blob());

    try {
        await waitForImageDecode(objectUrl);
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }

    return objectUrl;
};

const loadPrintableImage = async (image) => {
    const sourceUrl = getPrintableImageSource(image);

    if (!sourceUrl) return {src: "", status: "missing"};

    if (sourceUrl.startsWith("blob:") || sourceUrl.startsWith("data:")) {
        await waitForImageDecode(sourceUrl);
        return {src: sourceUrl, status: "loaded"};
    }

    try {
        const objectUrl = await fetchImageAsObjectUrl(sourceUrl);
        return {src: objectUrl, status: "loaded", objectUrl};
    } catch (fetchError) {
        try {
            await waitForImageDecode(sourceUrl);
            return {src: sourceUrl, status: "loaded"};
        } catch (imageError) {
            console.error("Erro ao carregar imagem para impressão.", fetchError, imageError);
            return {src: "", status: "error"};
        }
    }
};

const mapWithConcurrency = async (items, limit, mapper) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const workers = Array.from({length: Math.min(limit, items.length)}, async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            results[currentIndex] = await mapper(items[currentIndex], currentIndex);
        }
    });

    await Promise.all(workers);

    return results;
};

export const preparePrintableImages = async (passages = []) => {
    const imageCache = new Map();

    return mapWithConcurrency(passages, MAX_PRINT_IMAGE_CONCURRENCY, async (passage) => {
        const image = passage?.imageUrl;

        if (!imageCache.has(image)) {
            imageCache.set(image, loadPrintableImage(image));
        }

        const imageResult = await imageCache.get(image);

        return {
            ...passage,
            printImageObjectUrl: imageResult.objectUrl ?? null,
            printImageSrc: imageResult.src,
            printImageStatus: imageResult.status
        };
    });
};

export const releasePrintableImages = (passages = []) => {
    const objectUrls = new Set();

    passages.forEach((passage) => {
        if (passage?.printImageObjectUrl) objectUrls.add(passage.printImageObjectUrl);
    });

    objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
};
